// Renders the currently selected node or connection. Shows details, edits send
// parameters (level/pan/pre-post), removes a connection, and (no selection)
// lists recent plans for quick reopen.

import type { DeviceModel, ModelId, NodeKind } from "../models/types";
import { factoryNodeParams } from "../models/initial-state";
import { fullLabel, parseRef } from "../models/types";
import { sideLabel } from "../models";
import { displayedSide, shownStage } from "../core/stereo-side";
import { inputShare } from "./input-share";
import type { ConnParams, JackSide, NodeParams, Plan, PlanConnection, SsmcsParams } from "../core/plan";
import { clipNodeName, jackKey, LEVEL_MIN_DB, LEVEL_OFF_DB, processorOn, SSMCS_INITIAL } from "../core/plan";
import { LEVEL_POS_MAX, levelToPos, posToLevel, stepLevel } from "../core/levels";
import { hiZPatch, inputOnRefused, jackGainRange, jackIndex } from "../core/input-lock";
import { formatHz, fxEffectTypes, resolveFxEffectType } from "../core/control/fx-effect";

import {
  isFixedConnection,
  isLastRequiredSource,
  monoPairsInto,
  pairPrimary,
  sendHasOn,
  sendIsPreFader,
  sendTapWritable,
} from "../core/routing";
import {
  effectiveInsertFx,
  busBalance,
  busFader,
  busMasterOn,
  channelControl,
  channelDynamics,
  channelSections,
  colorControl,
  duckerControl,
  fxChannelIndex,
  inputEq,
  insertFxControl,
  isStereoChannel,
  oscAssign,
  outputEq,
} from "../core/control/translate";
import {
  COMP_EQ_COMP_FIRST,
  COMP_EQ_OPTIONS,
  INSERT_FX_NONE,
  OSC_MODE_BURST,
  OSC_MODE_OPTIONS,
  OSC_MODE_SINE,
  REC_POINT_DEFAULT,
  recPointOptionsFor,
  BUS_TYPE_VARI,
  BUS_TYPE_OPTIONS,
  SD_REC_TRACK_COUNT_DEFAULT,
  SD_REC_TRACK_COUNT_OPTIONS,
  SIGNAL_TYPE_OPTIONS,
  PAN_BAL_PAN,
  PAN_BAL_OPTIONS,
  COMP_EQ_SSMCS,
  COLOR_OFF,
  COLOR_PALETTE,
  planColorHex,
  DELAY_FRAME_RATE_OPTIONS,
  DELAY_FRAME_RATE_DEFAULT,
  insertFxEngaged,
} from "../core/control/params";
import {
  D_GAIN_MAX_DB,
  D_GAIN_MIN_DB,
  HPF_FREQ_DEFAULT_HZ,
  HPF_FREQ_MAX_HZ,
  HPF_FREQ_MIN_HZ,
  HPF_FREQ_STEP_HZ,
  PAN_MIN,
  PAN_MAX,
  DELAY_TIME_MIN_MS,
  DELAY_TIME_MAX_MS,
  DELAY_TIME_GRID_MS,
  delayTimeToVd,
  vdToDelayTime,
  PHONES_LEVEL_MIN,
  PHONES_LEVEL_MAX,
  PHONES_LEVEL_DEFAULT,
} from "../core/control/vd";
import {
  channelDuckerOn,
  channelEqUnavailable,
  duckerBypassCandidates,
  duckerBypassWarnings,
  formatRate,
  insertFxRateLock,
  insertFxMenu,
  isMonitorBus,
  canPatchFromMonitor,
  nodeRateDisabled,
  outputMono,
  rateConstraints,
  trackCountCeiling,
  trackCountOptionsAt,
} from "../core/constraints";
import { getSettings } from "../core/settings";
import type { RecentEntry } from "../core/storage";
import type { ConnectOrigin, Selection } from "./graph";
import { WIRE_GROUP } from "./graph";
import { setLevelText, setNamedText } from "./glyph";
import {
  holdInertOnBlur,
  isHoldingInert,
  labelId,
  mouseMovedUnpressed,
  onInertHoldsEnd,
  onWheelStep,
  scrubFloat,
  wheelStep,
} from "./dom";
import { dynOpenLabel } from "./dyn-registry";
import { insertFxScreenFamily } from "./insert-fx-screen";
import type { DynKind } from "./dyn-registry";
import { EQ_FREQ_POS_MAX, eqFreqToPos, eqPosToHz, formatDb, formatGainDb, formatPan } from "./inspector-format";
import { clearSectionOverride, recordSectionOpen, resolveSectionOpen } from "./inspector-sections";
import { isBalanceChannel, sendFields, sendlessNote } from "./send-fields";
import type { ParamField } from "./send-fields";
import { parkOutgoingInsertFxParams, seedInsertFxParams } from "./insert-fx-model";
import { t } from "../i18n";
import type { Messages } from "../i18n/en";

export interface InspectorActions {
  onDeleteConnection: (from: string, to: string) => void;
  onUpdateParams: (from: string, to: string, patch: ConnParams) => void;
  /** `written` names what the edit ASSERTED, as dotted paths (`"osc.on"`), for the
   *  funnel's write witness. Give it wherever the patch REBUILDS a nested group: the
   *  patch key alone names the whole group, which claims every sibling the rebuild
   *  merely copied. Absent means the patch's own keys, which is right for a scalar.
   *  `defaults` names, the same way, the values the edit put in as a type's defaults rather
   *  than as anything the operator chose — an effect selection's seeded engine slots. */
  onUpdateNodeParams: (
    id: string,
    patch: NodeParams,
    written?: readonly string[],
    defaults?: readonly string[],
  ) => void;
  /** Set a MIX bus's BUS Type. Separate from `onUpdateNodeParams` because the unit answers it
   *  outside the bus's own params: it resets every send into the bus, and FIXED turns the
   *  bus's Pan Link off. */
  onSetBusType: (id: string, busType: number) => void;
  /** Set an FX channel's EFFECT TYPE. Separate from `onUpdateNodeParams` because the unit refills
   *  the slots the incoming type uses with that type's factory values, and the write path has to
   *  know the plan took them. */
  onSetFxEffectType: (id: string, type: number) => void;
  onRenameNode: (id: string, name: string) => void;
  onRecolorNode: (id: string, color: string | null) => void;
  onOpenRecent: (path: string) => void;
  onHideNode: (id: string) => void;
  /** Open the GATE tuning screen for a MONO IN channel. */
  onOpenDynScreen: (kind: DynKind, id: string) => void;
  /** Show `side` of a stereo channel, as its CONSOLE name area does. */
  onSwitchSide: (channelId: string, side: JackSide) => void;
  onClose: () => void;
  /** The node's jacks a wire can be drawn from, with where each can go: the keyboard's way
   *  to connect, offered in the Routing section. */
  connectOrigins: (id: string) => ConnectOrigin[];
  /** Draw the wire from `origin` to `other`, as the board's drag does. */
  onConnect: (origin: ConnectOrigin, other: string) => void;
  /** Whether the board draws this wire; only such a wire is selectable from the panel. */
  wireDrawn: (from: string, to: string) => boolean;
  /** Select a wire, as a press on it does: the keyboard's way to its panel and its Delete. */
  onSelectConnection: (from: string, to: string) => void;
  /** The node's [CUE], which is outside the plan: null for a node that has none, else whether
   *  it is cued and whether a press switches it (a Live-sync session is up). */
  cueOf: (id: string) => CueRowState | null;
  /** Whether MIC/LINE jack `jack` (0 = the first) has its Clip Safe holding the gain down, as the
   *  unit last announced it. */
  clipSafeEngaged?: (jack: number) => boolean;
  /** Switch the node's [CUE], as its CONSOLE chip does. */
  onSetCue: (id: string, on: boolean) => void;
}

/** The nodes the panel's rendering is derived from — not the nodes the SELECTION names,
 *  which is a smaller set. A node selection reads that node's params; a connection reads
 *  BOTH endpoints' — the destination bus's BUS Type / Pan Link decide which send controls
 *  exist at all (mixSendLocks). What the selection does not name is here too: the monitor
 *  an analog output reports MONO from, and the DUCKER nodes below.
 *  Exported so a caller holding a changed node can ask whether the panel it is not
 *  rendering has gone stale, instead of restating the footprint at the call site. */
export function inspectorNodes(model: DeviceModel, plan: Plan, selection: Selection): string[] {
  // Ducker nodes are read in two places, neither of them the selection's own rows, and
  // both are narrowed to the duckers that would actually change what is drawn — an
  // arbitrary MIDI message can reach this branch holding one, and a ducker the panel does
  // not read would then rebuild it for a picture that did not move:
  //
  //   the ducker-bypass warning card, which is plan-wide and is drawn for every
  //   selection, including none — narrowed to the duckers the card is ABOUT (their host
  //   channel carries the USB tap), asked of the same predicate the card itself asks, and
  //   asked without the on-state, since a card appears when one turns ON;
  //
  //   the note under a send's own controls that a PRE tap sits ahead of the host
  //   channel's Ducker — that host is the wire's source, but the ducker hangs off it as a
  //   node of its own, which is the one the note is really read from.
  //
  // Neither is bounded by an operator's press. `duckerOn` is a toggle, and an edge
  // binding — the default — flips on every CC value >= 64, so one knob sweep bound to it
  // applies 64 times; naming a ducker the panel does not read would repaint at this
  // branch's ~20 Hz for the whole sweep, which is the cost the monitor below narrows for.
  //
  // The card's own preference (Preferences > Warnings) is deliberately not consulted: with
  // it off the card is not drawn and its duckers are named anyway, which over-names rather
  // than under-names — a rebuild that changes nothing, against a stale panel.
  const duckers = duckerBypassCandidates(model, plan).map((c) => c.ducker);
  if (!selection) return duckers;
  if (selection.type !== "node") {
    const { from, to } = selection;
    const host = parseRef(from).nodeId;
    // Asked of the wire, not just of its source: the note is drawn under a FIXED wire
    // carrying a tap, and only on PRE, so every other wire out of the same channel would
    // name its ducker for a note it cannot draw.
    //
    // The answer reads a connection param and the destination's BUS Type (a FIXED bus
    // takes the send after the fader), and stays current because nothing caches it —
    // main.ts recomputes this on each reflect, and the destination is named below — and
    // because each writer of a tap already repaints: `onUpdateParams` calls
    // refreshInspector for a `tap` patch, the MIDI tap control is owned by the source
    // channel (midi/controls.ts stamps `node: id`), which is named below
    // unconditionally, and no tap param carries follow: "direct", so a device-side
    // change lands on the scoped/full branch that refreshes wholesale.
    const conn = plan.connections.find((c) => c.from === from && c.to === to);
    const drawsNote = isFixedConnection(model, from, to) && conn !== undefined && sendIsPreFader(model, plan, conn);
    const own = drawsNote
      ? model.nodes
          .filter((n) => n.kind === "ducker" && n.attachTo === host && !duckers.includes(n.id))
          .map((n) => n.id)
      : [];
    return [host, parseRef(to).nodeId, ...duckers, ...own];
  }
  // An analog output's MONO row reports a MONITOR bus's switch, so a change on a node
  // the panel is not "showing" moves what it draws — exactly the case this function
  // exists for. Narrowed to the monitor the plan actually patches from, not both:
  // `bus.mon1/2` carry three params that follow DIRECTLY (MONITOR_ON, MONITOR_LEVEL,
  // PHONES_LEVEL), so naming a monitor the output does not read makes a knob turn on
  // the unit rebuild this panel at the follow rate — replaceChildren, the plan-wide
  // duckerBypassWarnings sweep and a focus restore, ~20 times a second, for a row
  // whose content did not change. MONO itself is not in that direct set and arrives
  // on the full-reflect path, which refreshes the panel anyway; what this line buys
  // is the MIDI route, where the plan is written without a device read.
  // The selected node is prepended rather than pushed, so a ducker that is also a
  // candidate above appears once.
  const listed = duckers.includes(selection.id) ? duckers : [selection.id, ...duckers];
  // A channel's INPUT rows are the jack's or the source's it reads, so a change there — a
  // direct A.Gain follow, a MIDI apply, another channel's edit — moves what this panel draws.
  const stage = shownStage(model, plan, selection.id);
  const own = stage.kind === "none" ? listed : [...listed, stage.node];
  if (!canPatchFromMonitor(model, selection.id)) return own;
  const mono = outputMono(plan, selection.id);
  return mono.via === "monitor" ? [...own, mono.monitorId] : own;
}

/** The gate a rebuild of `host` asks before running, so it cannot land inside an IME
 *  composition. `replaceChildren` destroys one — measured in the real WKWebView, with the
 *  panel repainted at the 20 Hz a device-driven reflect produces (ui/keyprobe.ts, F4):
 *  typing `nihonngo` into a node name produced `nいhおnngお` and EIGHT compositionstarts
 *  for eight keystrokes. Not "the interim kana are committed and composition restarts":
 *  the romaji buffer that spans keystrokes goes with the element, so a consonant that had
 *  no vowel yet lands as literal ASCII and only a bare vowel survives as kana. Focus and
 *  caret are not the problem — they are carried across the rebuild and every character
 *  landed in the right place. A rebuild asked for while a composition is in flight is
 *  remembered and run once it ends, rather than dropped: the plan has already changed and
 *  the panel would otherwise keep showing the value before it. Same repaint rate through
 *  this gate: one compositionstart, one compositionend, `日本語` intact, 67 of 397
 *  repaints held.
 *
 *  Confirmed on the device path itself, which is what the synthetic repaint stands in
 *  for: sweeping CH 1's A.Gain (`HA_GAIN`, a direct follow) with the channel selected
 *  repaints this panel 6 times. With a composition open, all 6 are held and exactly ONE
 *  runs — at the commit — and the name arrives whole.
 *
 *  The composition events bubble, so the host keeps listening across every rebuild.
 *  `focusout` backstops a field that loses FOCUS — not one that is removed: the same
 *  measurement recorded zero compositionend AND zero focusout at the host across 349
 *  rebuilds that took the composing field away. Whether WebKit fires neither, or fires
 *  them at a node already detached, is the same thing to a listener here. So nothing
 *  releases the gate if the composing field is removed by some other path, and what makes
 *  that unreachable is that `renderInspector` has exactly ONE call site and it is behind
 *  this gate. A second one would latch `composing` for the rest of the session and the
 *  panel would silently stop updating. */
export function compositionGate(host: HTMLElement, rebuild: () => void): { held: () => boolean; reset: () => void } {
  let composing = false;
  let pending = false;
  /** Pointers pressed inside the host and not yet released. */
  const pressing = new Set<number>();
  // Runs the held rebuild once the gate is no longer busy — asked on the event that
  // ENDS the busy state, by which point it has already ended. A composition clears its
  // own flag here.
  //
  // A PICKER does not clear itself, and `pickerClosed` is what says so. Its "open" is
  // read off focus, and a select KEEPS focus after a choice is made in it, so a flush
  // that re-asks `openPicker()` on `change` is asking about the picker that just closed
  // and answering yes — deferring the rebuild to whatever the operator happened to do
  // next. The event is the dismissal, so the flush it triggers takes that as read.
  //
  // Every select that changes WHICH controls the panel shows depends on this — Insert
  // FX, COMP/EQ Type and Signal Type. Without it each one writes the plan and leaves the
  // old set on screen: an insert effect is chosen and neither the bypass switch nor the
  // launcher appears, which between them are the whole route to the tuning screen.
  const flush = (pickerClosed = false): void => {
    if (!pending) return;
    if (composing || pressing.size > 0 || isHoldingInert() || (!pickerClosed && openPicker())) return;
    pending = false;
    rebuild();
  };
  const end = (pickerClosed = false): void => {
    composing = false;
    flush(pickerClosed);
  };
  // An open `<select>` picker is the second kind of in-flight input a rebuild destroys,
  // and `replaceChildren` closes it instantly — so while any followed parameter on the
  // node moved (an external MIDI sweep, a device-side knob), the reflect ran at up to
  // 20 Hz and the Rec Point / Signal Type / INS FX dropdowns simply could not be opened.
  // Focus is restored to the fresh select afterwards, but an open picker is not
  // restorable state.
  //
  // Read off `document.activeElement` rather than tracked with a flag: a picker fires no
  // "opened" event for the page to latch, while focus is observable — a select inside
  // this host holds it for as long as its popup can be open. It ends on the same
  // `focusout`, and on `change`, which is what a picker dismissal produces.
  const openPicker = (): boolean =>
    document.activeElement instanceof HTMLSelectElement && host.contains(document.activeElement);
  // A row held inert is the third kind of in-flight input a rebuild destroys, and the
  // worst of them: replacing it hands the still-held pointer a live control, which is the
  // state the hold exists to prevent. Same seam, one more reason.
  //
  // A press that began inside the host is the fourth. The click a press produces is
  // dispatched at its release, to the element the press began on, so a rebuild between
  // the two removes that element and the click reaches nothing — a button, a segmented
  // bar's segment, a section header, the field a press was focusing. Held from the
  // pointerdown until that click has reached its target's own handlers.
  const busy = (): boolean => composing || pressing.size > 0 || openPicker() || isHoldingInert();
  // …and the hold has no end event of its own: a hold ends on a pointer release this host
  // never sees, so nothing here would fire and a rebuild held during it would wait for
  // whatever the operator happened to do next. `flush` rather than `end`,
  // which also clears `composing` — a backstop the two input kinds need and this one must
  // not take, since a hold can end while a composition is genuinely still in flight.
  // Each of these is wrapped rather than passed straight in: `flush` and `end` now take
  // a flag, and a listener is handed the EVENT as its first argument — which is an
  // object, so every one of them would read as "the picker closed" and the check the
  // other two paths depend on would be gone.
  onInertHoldsEnd(() => flush());
  // Each press is tracked by its pointer, and ends at that pointer's click, on this host's
  // bubble phase — after the target's own handlers, so the rebuild the click itself asks
  // for runs there once — and, for a press that produces no click here (released outside
  // the host, a secondary button, a handler that stops the click), in the task after its
  // own pointer's release, at its own mouse moving with no button held, or when the window
  // comes back from a release it never heard. The click is dispatched in the same task as
  // the release producing it, so that task comes after it. Another pointer's release ends
  // nothing: a mouse clicking elsewhere while a finger holds a control here is not that
  // finger lifting. A pointer pressed outside the host holds nothing here, so a press never
  // seen released elsewhere — a synthetic one, a release another window took — cannot keep
  // the panel from updating.
  // A press on a `<select>` is left out: the picker it opens holds the panel by focus,
  // and its `change` releases that hold whether or not the page hears the press's release.
  /** Pointers released whose click has not reached the host yet. */
  const lifted = new Set<number>();
  const release = (id: number): void => {
    lifted.delete(id);
    if (pressing.delete(id) && pressing.size === 0) flush();
  };
  const releaseAll = (): void => {
    lifted.clear();
    if (pressing.size === 0) return;
    pressing.clear();
    flush();
  };
  const releaseNextTask = (e: PointerEvent): void => {
    if (!pressing.has(e.pointerId)) return;
    lifted.add(e.pointerId);
    setTimeout(() => release(e.pointerId), 0);
  };
  host.addEventListener(
    "pointerdown",
    (e) => {
      if (e.target instanceof Element && e.target.closest("select")) return;
      pressing.add(e.pointerId);
    },
    true,
  );
  // The click belongs to a pointer already released, so it ends those presses and none
  // still down.
  host.addEventListener("click", () => {
    for (const id of [...lifted]) release(id);
  });
  window.addEventListener("pointerup", releaseNextTask, true);
  window.addEventListener("pointercancel", releaseNextTask, true);
  // The native context menu takes a right press's release, and no click follows a move, so
  // the move with no button held releases that mouse's press at once.
  window.addEventListener("pointermove", (e) => void (mouseMovedUnpressed(e) && release(e.pointerId)), true);
  window.addEventListener("focus", releaseAll);
  host.addEventListener("compositionstart", () => {
    composing = true;
  });
  host.addEventListener("compositionend", () => end());
  host.addEventListener("change", () => end(true));
  // A focus move releases the hold in the NEXT task rather than inside the focusout. The
  // focusout fires partway through the gesture that moves the focus — a Tab before the
  // focus lands on the next control, a click or a tap before the click event — and a
  // rebuild there removes the control the gesture is going to: the Tab's focus lands on
  // nothing, and the click on nothing. One task later the focus has landed, which is what
  // the rebuild carries across, and a pointer press still down holds it as above.
  host.addEventListener("focusout", () => {
    composing = false;
    setTimeout(() => flush(), 0);
  });
  return {
    held: () => {
      if (!busy()) return false;
      pending = true;
      return true;
    },
    // The panel is about to show a DIFFERENT node. Everything this gate protects belongs
    // to the one being left — an open picker, a composition, a row held under a pointer —
    // so there is nothing left to outrank the change, and holding it leaves the panel
    // describing a node the operator has moved away from.
    //
    // The flags are dropped rather than left standing, and that is the load-bearing half:
    // the caller rebuilds without asking, which removes the composing field, and the end
    // event for a composition whose field is gone may never arrive. Left set, `composing`
    // would latch for the rest of the session and the panel would stop updating at all —
    // the failure this file's header names. A press stays held: the caller's rebuild does
    // not end it, and its own release does.
    reset: () => {
      composing = false;
      pending = false;
    },
  };
}

export function renderInspector(
  host: HTMLElement,
  model: DeviceModel,
  plan: Plan,
  selection: Selection,
  actions: InspectorActions,
  recent: RecentEntry[] = [],
  // While live-connected, params the device cannot accept from software (CH → FX
  // tap) are shown read-only. Off (the default) in the pure planner, where the
  // plan is intent and every param stays editable.
  liveActive = false,
): void {
  host.replaceChildren();
  const labelOf = (nodeId: string): string => {
    const node = model.nodes.find((n) => n.id === nodeId);
    return node ? fullLabel(node) : nodeId;
  };
  const endpointLabel = (r: string): string => labelOf(parseRef(r).nodeId);

  const m = t();
  // Mobile-only dismiss control (the bottom-sheet pull tab's close affordance);
  // hidden on the desktop side panel via CSS.
  host.append(closeButton(m.inspector.close, actions.onClose));
  // Warning cards are display-only and preference-gated (Preferences > Warnings);
  // the behavior locks they describe (rate-disabled nodes / stereo EQ) stay on.
  const constraints = rateConstraints(model, plan.sampleRate);
  if (getSettings().warnRate && constraints.warnings.length)
    host.append(
      warningBox(
        m.warning.title,
        constraints.warnings.map((w) => m.warning[w]),
      ),
    );
  // A live signal-flow caution (not rate-dependent): a channel with its Ducker on
  // that is also tapped straight to a USB direct out never carries the duck.
  const duckerBypass = getSettings().warnDucker ? duckerBypassWarnings(model, plan) : [];
  if (duckerBypass.length)
    host.append(
      warningBox(
        m.warning.duckerTitle,
        duckerBypass.map((id) => m.warning.duckerBypass(labelOf(id))),
      ),
    );

  if (!selection) {
    host.append(heading(m.inspector.title), hint(m.inspector.hint));
    host.append(legendBlock(m));
    if (recent.length) {
      host.append(subheading(m.inspector.recentPlans));
      for (const entry of recent) host.append(recentRow(entry, actions.onOpenRecent));
    }
    return;
  }

  if (selection.type === "node") {
    const node = model.nodes.find((n) => n.id === selection.id);
    if (!node) return;
    // The heading keeps the device identity (CH 1 …) so you always know which
    // physical strip you are patching; the Name field below holds the override.
    host.append(heading(fullLabel(node)), field(m.inspector.type, nodeKindLabel(node.kind)));

    // MAIN / LINE OUT: a standing MONO row. The output itself has no MONO control
    // — the device puts [MONO] on the MONITOR buses — so what this output can do
    // is decided by what it is patched from, and the row is the one place that
    // says so before the patch is drawn. Read-only on purpose: the switch keeps
    // its single home on the MONITOR node, and the row names which one owns it.
    if (canPatchFromMonitor(model, node.id)) {
      const mono = outputMono(plan, node.id);
      host.append(
        field(
          m.inspector.mono,
          mono.via === "monitor"
            ? m.inspector.monoVia(mono.on ? m.inspector.on : m.inspector.off, labelOf(mono.monitorId))
            : m.inspector.monoUnavailable,
        ),
      );
      // The unavailable state is the one that needs the way out spelled; the
      // available one already names the node the switch lives on.
      if (mono.via === "none") host.append(hint(m.inspector.patchNoMono));
    }

    // Channel / bus strips carry a user-editable name (the device's CH SETTING
    // name); empty falls back to the model's default label.
    if (node.kind === "channel" || node.kind === "bus") {
      host.append(
        textInput(
          m.inspector.name,
          plan.nodeNames[node.id] ?? "",
          fullLabel(node),
          (v) => actions.onRenameNode(node.id, v),
          clipNodeName,
        ),
      );
    }
    // Color swatches only for nodes the device actually colors (input channels +
    // STEREO / MIX / FX / STREAMING buses). Monitor and OSC buses have no device
    // color param, so they get no picker.
    if (colorControl(model, node.id)) {
      host.append(colorSwatches(m.inspector.color, plan.nodeColors[node.id], (c) => actions.onRecolorNode(node.id, c)));
    }

    // The unit's [CUE], as the node's CONSOLE chip switches it: live-session state rather than
    // the plan's, so it switches only while a Live-sync session is up.
    const cue = actions.cueOf(node.id);
    if (cue) host.append(cueRow(node.id, cue, actions, m));

    // Rec Point (CH SETTING): the recording / direct-out tap stage. MONO IN
    // offers all five stages; ST IN only the two `stereo` options. Channels only.
    if (node.kind === "channel") {
      // MONO IN exposes all five tap stages; ST IN only the `stereo` ones. In
      // SSMCS mode the device drops PRE EQ (no discrete EQ stage to tap ahead of).
      const isMono = channelControl(model, node.id)?.hasMicStrip === true;
      const inSsmcs = isMono && plan.nodeParams[node.id]?.compEqType === COMP_EQ_SSMCS;
      const recOptions = recPointOptionsFor(!isMono, inSsmcs);
      host.append(
        enumSelect(m.inspector.recPoint, recOptions, plan.nodeParams[node.id]?.recPoint ?? REC_POINT_DEFAULT, (v) =>
          actions.onUpdateNodeParams(node.id, { recPoint: v }),
        ),
      );
    }

    // Signal Type (CH SETTING): a MONO IN pair (CH1/2, CH3/4) is STEREO-linked or
    // MONO x 2. Stored on the pair's primary (odd) channel, so either member edits
    // the same value. STEREO additionally exposes the PAN / BAL mode.
    const primary = pairPrimary(model, node.id);
    if (primary) {
      const pnp = plan.nodeParams[primary] ?? {};
      const linked = pnp.stereoLink ?? false;
      host.append(
        enumSelect(m.inspector.signalType, SIGNAL_TYPE_OPTIONS, linked ? 1 : 0, (v) =>
          actions.onUpdateNodeParams(primary, { stereoLink: v === 1 }),
        ),
      );
      if (linked) {
        host.append(
          enumSelect(m.inspector.panBal, PAN_BAL_OPTIONS, pnp.panBal ?? PAN_BAL_PAN, (v) =>
            actions.onUpdateNodeParams(primary, { panBal: v }),
          ),
        );
      }
    }

    // BUS Type / Pan Link (CH SETTING): MIX 1 / MIX 2 only. FIXED makes every
    // send into the bus a fixed level; Pan Link ties each send pan to its source's
    // own PAN / BAL. Both gate the per-send controls (connection panel). The Pan Link
    // switch is offered under VARI only, as on the unit's screen. Setting BUS Type here
    // resets the bus's sends and, to FIXED, turns Pan Link off (onSetBusType).
    if (node.id === "bus.mix1" || node.id === "bus.mix2") {
      const bnp = plan.nodeParams[node.id] ?? {};
      const busType = bnp.busType ?? BUS_TYPE_VARI;
      host.append(enumSelect(m.inspector.busType, BUS_TYPE_OPTIONS, busType, (v) => actions.onSetBusType(node.id, v)));
      if (busType === BUS_TYPE_VARI) {
        host.append(
          boolToggle(m.inspector.panLink, bnp.panLink ?? false, (v) =>
            actions.onUpdateNodeParams(node.id, { panLink: v }),
          ),
        );
      }
    }

    // microSD Rec header (out.sdrec): the Track Count gates how many track-pair
    // slots show. Never pushed to the device (the broker refuses every value above
    // two tracks), so it is editable in the planner but disabled with a note while
    // live-connected (mirrors the CH → FX tap pattern); the per-track source assign
    // is done by canvas wires.
    if (node.id === "out.sdrec") {
      const count = plan.nodeParams[node.id]?.sdRecTrackCount ?? SD_REC_TRACK_COUNT_DEFAULT;
      // Only what the rate can carry. Offering more would let a plan be authored that the
      // unit cannot hold and no write could establish — the rate change already lowered the
      // count itself (setPlanSampleRate), so this is the menu agreeing with the value.
      // Plus the value the plan actually holds, when the rate would not offer it: a device
      // read reports what the UNIT said, and the unit may still be on a rate this plan has
      // left. A menu without its own value in it leaves the control with nothing selected,
      // which reads as empty rather than as "16, which this rate cannot carry".
      const allowed = trackCountOptionsAt(plan.sampleRate);
      const trackOptions = allowed.some((o) => o.value === count)
        ? allowed
        : [...allowed, { value: count, label: String(count) }].sort((a, b) => a.value - b.value);
      host.append(
        enumSelect(
          m.inspector.sdRecTrackCount,
          trackOptions,
          count,
          (v) => actions.onUpdateNodeParams(node.id, { sdRecTrackCount: v }),
          liveActive,
        ),
      );
      if (liveActive) host.append(hint(m.inspector.sdRecTrackCountLive));
      // Why the menu is short. Without this the missing entries read as a defect, and the
      // one fact the operator needs — that going back down does not bring them back — has
      // nowhere else to appear on this panel.
      if (trackOptions.length < SD_REC_TRACK_COUNT_OPTIONS.length) {
        host.append(hint(m.inspector.sdRecTrackCountRate(trackCountCeiling(plan.sampleRate))));
      }
    }

    // After a device readback, a node in plan.unreadNodes still shows its plan
    // value (a read failed on it); warn that its values are not the device's.
    // No provenance (a plan never fetched) shows nothing.
    if (plan.unreadNodes?.has(node.id)) {
      host.append(notReadBadge(m.inspector.notReadFromDevice));
    }

    const outgoing = plan.connections.filter((c) => parseRef(c.from).nodeId === node.id);
    const incoming = plan.connections.filter((c) => parseRef(c.to).nodeId === node.id);
    // Routing lists default collapsed — wiring is done on the canvas, so the
    // inspector keeps this folded away behind a count summary. A header node
    // (microSD Rec) takes no direct wire of its own, so it shows no routing list.
    // It is also the keyboard's way to wire: each wire the board draws is a button that
    // selects it (its panel carries the delete), and each jack offers what it can connect
    // to.
    if (!node.header) {
      const { el, body } = section(m.inspector.routing, { open: false, key: "routing" });
      const origins = actions.connectOrigins(node.id);
      const connect = (origin: ConnectOrigin): void => {
        if (!origin.targets.length) return;
        const label =
          origin.dir === "in"
            ? m.inspector.connectSource
            : origin.tap
              ? m.inspector.connectRecPoint
              : m.inspector.connectOutput;
        body.append(connectControl(label, origin, endpointLabel, actions, m));
      };
      body.append(subheading(m.inspector.inputsFrom(incoming.length)));
      for (const c of incoming) body.append(connRow(`${endpointLabel(c.from)} →`, c, actions));
      for (const o of origins) if (o.dir === "in") connect(o);
      body.append(subheading(m.inspector.outputsTo(outgoing.length)));
      for (const c of outgoing) body.append(connRow(`→ ${endpointLabel(c.to)}`, c, actions));
      for (const o of origins) if (o.dir === "out") connect(o);
      host.append(el);
    }

    // Channel node device parameters: ON (mute) and HPF. Stored per node id, so
    // they edit plan.nodeParams rather than a wire. Defaults match the device
    // (channel on, HPF off) until a fetch or edit sets them explicitly.
    if (node.kind === "channel") {
      const np = plan.nodeParams[node.id] ?? {};
      // Channel ON (mute) leads the parameters, matching the bus / FX / MONITOR
      // inspectors — every node now puts its on/off at the top of the group.
      host.append(
        boolToggle(m.inspector.channelOn, np.on ?? true, (v) => actions.onUpdateNodeParams(node.id, { on: v })),
      );
      const cc = channelControl(model, node.id);
      const compEqType = np.compEqType ?? COMP_EQ_COMP_FIRST;
      const inSec = section(m.inspector.inputSection, { key: "input" });
      // Flow the short INPUT toggles (+48V / Hi-Z / Clip Safe / Ф / HPF) into two
      // columns; sliders and the select span full width (style.css :has rule).
      inSec.el.dataset.cols = "2";
      const input = inSec.body;

      // INPUT screen order (device top-left → bottom-right): +48V, A.Gain, HI-Z,
      // Clip Safe, Ф, HPF, HPF Freq. +48V, A.Gain, HI-Z and Clip Safe are the jack's this
      // channel reads, and D.Gain the digital source's — written there, so every channel
      // reading it applies the value; a channel with no source shows none of them. HI-Z
      // only on a jack that carries it. +48V and HI-Z are never turned on together: while one
      // is on, the other's toggle is read-only and says why (turning the lit one off stays
      // available).
      // A stereo channel shows one side's input stage and polarity, picked by the switch at
      // the top.
      if (isStereoChannel(node.id)) input.append(sideSwitch(model, node.id, actions, m));
      const stage = shownStage(model, plan, node.id);
      if (stage.kind !== "none" && plan.unreadNodes?.has(stage.node)) {
        input.append(notReadBadge(m.inspector.notReadFromDevice));
      }
      if (stage.kind === "jack") {
        const holder = stage.node;
        const jnp = plan.nodeParams[holder] ?? {};
        const key = (f: "gain" | "phantom" | "clipSafe" | "hiZ"): string => jackKey(f, stage.side);
        const flag = (f: "phantom" | "clipSafe" | "hiZ"): boolean => Boolean((jnp as Record<string, unknown>)[key(f)]);
        input.append(
          boolToggle(
            m.inspector.phantom,
            flag("phantom"),
            (v) => actions.onUpdateNodeParams(holder, { [key("phantom")]: v }),
            inputOnRefused(model.id, stage, jnp, "phantom") ? m.inspector.phantomLockedByHiZ : undefined,
          ),
        );
        // A.Gain: -8..+70, -8..+40 while the jack's HI-Z is on.
        const range = jackGainRange(model.id, stage, jnp)!;
        const held = (jnp as Record<string, unknown>)[key("gain")];
        input.append(
          gainControl(
            m.inspector.gainAnalog,
            range.minDb,
            range.maxDb,
            typeof held === "number" ? held : factoryGain(model.id, holder, key("gain"), range.minDb),
            (v) => actions.onUpdateNodeParams(holder, { [key("gain")]: v }),
          ),
        );
        if (stage.hiZ) {
          input.append(
            boolToggle(
              m.inspector.hiZ,
              flag("hiZ"),
              // The cap reads the plan, not the `jnp` this row was built from: a gain slide
              // does not rebuild the panel (the slider has to keep the pointer), so the
              // snapshot holds the gain as it was when the node was selected.
              (v) => actions.onUpdateNodeParams(holder, hiZPatch(plan.nodeParams[holder], stage.side, v)),
              inputOnRefused(model.id, stage, jnp, "hiZ") ? m.inspector.hiZLockedByPhantom : undefined,
            ),
          );
        }
        const clipSafe = boolToggle(m.inspector.clipSafe, flag("clipSafe"), (v) =>
          actions.onUpdateNodeParams(holder, { [key("clipSafe")]: v }),
        );
        const jack = jackIndex(stage);
        if (jack !== null) {
          clipSafe.dataset.clipSafeJack = String(jack);
          paintClipSafeRow(clipSafe, actions.clipSafeEngaged?.(jack) ?? false);
        }
        input.append(clipSafe);
      } else if (stage.kind === "digital") {
        // D.Gain: -24..+24, one value per source.
        const held = plan.nodeParams[stage.node]?.dGain;
        input.append(
          gainControl(
            m.inspector.gainDigital,
            D_GAIN_MIN_DB,
            D_GAIN_MAX_DB,
            held ?? factoryGain(model.id, stage.node, "dGain", 0),
            (v) => actions.onUpdateNodeParams(stage.node, { dGain: v }),
          ),
        );
      }
      // Polarity invert (Ф): one row, announced by its word. A stereo channel's inverts the
      // side it shows.
      const phase = cc?.phases.find((ph) => ph.side === "" || ph.side === displayedSide(node.id));
      if (phase) {
        input.append(
          boolToggle(
            m.inspector.phase,
            np[phase.key] ?? false,
            (v) => actions.onUpdateNodeParams(node.id, { [phase.key]: v }),
            undefined,
            undefined,
            m.inspector.phaseName,
          ),
        );
      }
      if (cc?.hasHpf) {
        input.append(
          boolToggle(m.inspector.hpf, np.hpf ?? false, (v) => actions.onUpdateNodeParams(node.id, { hpf: v })),
        );
        input.append(
          rangeSlider(
            m.inspector.hpfFreq,
            HPF_FREQ_MIN_HZ,
            HPF_FREQ_MAX_HZ,
            HPF_FREQ_STEP_HZ,
            np.hpfFreq ?? HPF_FREQ_DEFAULT_HZ,
            (v) => `${v} Hz`,
            (v) => actions.onUpdateNodeParams(node.id, { hpfFreq: v }),
          ),
        );
      }
      // COMP/EQ type (COMP->EQ vs SSMCS) — the CH SETTING bank selector that drives
      // which COMP/EQ controls appear below. MONO IN channels only. Each type's bank
      // is separate on the device and reloaded to factory on every switch; the app
      // wiring resets the destination bank (see resetCompEqBank in main.ts), so the
      // selector only declares the new type here.
      if (cc?.hasMicStrip) {
        input.append(
          enumSelect(m.inspector.compEqType, COMP_EQ_OPTIONS, compEqType, (v) =>
            actions.onUpdateNodeParams(node.id, { compEqType: v }),
          ),
        );
      }
      host.append(inSec.el);

      // SSMCS Main section (MONO IN, SSMCS mode): the [SSMCS] on/off and the launcher
      // for the morphing strip's MAIN face. Built here but inserted between the GATE
      // and COMP sections in the loop below.
      const ssmcs = cc?.hasMicStrip && compEqType === COMP_EQ_SSMCS;
      let ssmcsMasterEl: HTMLElement | null = null;
      if (ssmcs) {
        const son = np.ssmcs?.on ?? SSMCS_INITIAL.on;
        const { el, body } = section(m.inspector.ssmcs.title, { open: son, on: son, key: "ssmcsOn" });
        // Toggling the on flag drops any manual fold so the section reverts to
        // following the on-state, matching sectionToggle's contract.
        body.append(
          boolToggle(m.inspector.ssmcs.title, son, (v) => {
            clearSectionOverride("ssmcsOn");
            mergeSsmcs(actions, plan, node.id, { on: v });
          }),
        );
        body.append(dynLauncher("ssmcs", node.id, actions, m));
        ssmcsMasterEl = el;
      }

      // GATE / COMP / EQ sections in channel-strip order, each a collapsible
      // module matching the device's dedicated GATE / COMP / EQ screens. The
      // summary carries the section's ON led; an off section folds itself away.
      // Mono channels have all three; stereo channels expose only EQ. In SSMCS
      // mode the SSMCS Main section sits between GATE and COMP, and the COMP/EQ
      // sections render the morphing-strip controls. Default: EQ on, GATE/COMP off.
      const dyn = channelDynamics(model, node.id, compEqType);
      const ieq = inputEq(model, node.id, compEqType);
      // The stereo channels' EQ dies at 176.4 / 192 kHz (block diagram): lock the
      // section to OFF with a disabled toggle + tooltip, and drop the (now inert)
      // band editor. The plan's eqOn is left untouched so lowering the rate restores it.
      const eqLocked = channelEqUnavailable(node.id, plan.sampleRate);
      for (const sec of channelSections(model, node.id, compEqType)) {
        const locked = sec.key === "eqOn" && eqLocked;
        const on = locked ? false : processorOn(np, sec.key);
        const { el, body } = section(m.inspector[sec.key], { open: on, on, key: sec.key });
        body.append(
          sectionToggle(
            node.id,
            sec.key,
            on,
            actions,
            m.inspector[sec.key],
            locked ? m.inspector.eqRateLocked : undefined,
          ),
        );
        if (sec.key === "gateOn" && dyn) body.append(dynLauncher("gate", node.id, actions, m));
        else if (sec.key === "compOn" && ssmcs) body.append(dynLauncher("ssmcsComp", node.id, actions, m));
        else if (sec.key === "compOn" && dyn?.comp) body.append(dynLauncher("comp", node.id, actions, m));
        else if (sec.key === "eqOn" && ssmcs) body.append(dynLauncher("ssmcsEq", node.id, actions, m));
        else if (sec.key === "eqOn" && ieq && !locked) body.append(dynLauncher("eq", node.id, actions, m));
        host.append(el);
        // Insert the SSMCS Main section right after GATE (before COMP).
        if (sec.key === "gateOn" && ssmcsMasterEl) host.append(ssmcsMasterEl);
      }
    }

    // Ducker node: on/off + detail (threshold/range/attack/decay) for the
    // stereo-channel sidechain. Defaults off, so it is not dimmed like a muted
    // channel (which keys off `on`).
    if (node.kind === "ducker" && duckerControl(model, node.id)) {
      host.append(duckerBlock(node.id, plan.nodeParams[node.id] ?? {}, plan, actions, m));
    }

    // Bus output fader: STEREO master (581) and MIX 1/2 (674). Reuses
    // nodeParams.level. Both also carry an ON/OFF (STEREO_MASTER_ON 582 / MIX
    // OUT_MASTER_ON 675), edited here only — the CONSOLE shows it read-only. The
    // toggle leads the section as a "Channel" ON (like an FX channel).
    if (busFader(node.id)) {
      const np = plan.nodeParams[node.id] ?? {};
      const ps = section(m.inspector.parameters, { key: "params" });
      if (busMasterOn(node.id)) {
        ps.body.append(
          boolToggle(m.inspector.channelOn, np.on ?? true, (v) => actions.onUpdateNodeParams(node.id, { on: v })),
        );
      }
      ps.body.append(faderControl(np.level ?? 0, (v) => actions.onUpdateNodeParams(node.id, { level: v })));
      // Master balance (STEREO 583 / MIX 676): the bus output's L/R balance. The
      // device keeps the BALANCE label even under Pan Link (confirmed on URX44V),
      // so it is always "Balance".
      if (busBalance(node.id)) {
        ps.body.append(
          balanceControl(m.inspector.balance, np.pan ?? 0, (v) => actions.onUpdateNodeParams(node.id, { pan: v })),
        );
      }
      host.append(ps.el);
      // Output bus 4-band PEQ (STEREO 498-block single / MIX 591-block L/R-linked)
      // as a collapsible EQ module — its ON led (STEREO 498 / MIX 591, default on)
      // drives the fold, matching the channel EQ section.
      const oeq = outputEq(node.id);
      if (oeq) {
        const on = processorOn(np, "eqOn");
        const { el, body } = section(m.inspector.eqOn, { open: on, on, key: "eqOn" });
        body.append(sectionToggle(node.id, "eqOn", on, actions, m.inspector.eqOn));
        body.append(dynLauncher("eq", node.id, actions, m));
        host.append(el);
      }
    }

    // FX bus (FX 1 / FX 2): the FX channel's own ON / mute (param 338, per FX),
    // distinct from the per-channel FX sends feeding it. (Post Fader Send for FX
    // is a DAW-Integration-only setting outside the plan, shown on the SIM view, so
    // it is not modeled here.)
    const fxY = fxChannelIndex(node.id);
    if (fxY !== null) {
      // FX2 above 96 kHz: the bus is gone, so its switch and its effect show their values and
      // take no edit, as its CONSOLE strip does.
      const rateGone = nodeRateDisabled(node.id, plan.sampleRate);
      const ps = section(m.inspector.parameters, { key: "params" });
      ps.body.append(
        boolToggle(
          m.inspector.channelOn,
          plan.nodeParams[node.id]?.on ?? true,
          (v) => actions.onUpdateNodeParams(node.id, { on: v }),
          rateGone ? m.inspector.fx2RateLocked : undefined,
        ),
      );
      host.append(ps.el);
      host.append(fxEffectSection(node.id, fxY, plan, actions, m));
    }

    // Monitor bus ON (MONITOR_ON) + level (MONITOR_LEVEL) plus the CUE-interrupt /
    // MONO toggles. ON precedes the fader to match the device MONITOR screen order.
    if (isMonitorBus(node.id)) {
      const np = plan.nodeParams[node.id] ?? {};
      const ps = section(m.inspector.parameters, { key: "params" });
      ps.body.append(
        boolToggle(m.inspector.monitorOn, np.on ?? true, (v) => actions.onUpdateNodeParams(node.id, { on: v })),
      );
      ps.body.append(faderControl(np.level ?? 0, (v) => actions.onUpdateNodeParams(node.id, { level: v })));
      // PHONES output level: a unit-less 0.0..10.0 scale, independent of the
      // monitor fader (PHONES 1 ↔ mon1, PHONES 2 ↔ mon2 — same signal, own level).
      ps.body.append(
        rangeSlider(
          m.inspector.phonesLevel,
          PHONES_LEVEL_MIN,
          PHONES_LEVEL_MAX,
          0.1,
          np.phonesLevel ?? PHONES_LEVEL_DEFAULT,
          (v) => v.toFixed(1),
          (v) => actions.onUpdateNodeParams(node.id, { phonesLevel: v }),
        ),
      );
      ps.body.append(
        boolToggle(m.inspector.cueInterrupt, np.cueInterrupt ?? true, (v) =>
          actions.onUpdateNodeParams(node.id, { cueInterrupt: v }),
        ),
      );
      ps.body.append(
        boolToggle(m.inspector.mono, np.mono ?? false, (v) => actions.onUpdateNodeParams(node.id, { mono: v })),
      );
      host.append(ps.el);
      // The block diagram takes this monitor's PHONES pair AFTER the MONO block, so
      // switching MONO for a pair of speakers takes the headphones with it. Said only
      // while it is on: off, there is nothing for it to be about.
      //
      // On `host`, where every other hint goes, and NOT inside the section body —
      // measured with it there: the body's `> .param:last-child { border-bottom: none }`
      // stopped matching, so the MONO row's rule reappeared whenever MONO was on
      // (0px -> 1px), and the paragraph brought the UA's 1em margins (13px top and
      // bottom) into a body padded 6px. Section height went 358 -> 495.
      if (np.mono) host.append(hint(m.inspector.monoPhonesShared));
    }

    // Oscillator generator (bus.osc): on / level / mode / frequency. Frequency
    // shows only in Sine Wave mode; Width / Interval only in Burst Noise mode
    // (mode change relayouts, see main.ts).
    if (node.id === "bus.osc") {
      const osc = plan.nodeParams[node.id]?.osc ?? {};
      // Read the latest stored osc at edit time so a second field edit (the
      // inspector is not re-rendered between edits) does not clobber the first.
      const setOsc = (patch: Partial<typeof osc>): void =>
        actions.onUpdateNodeParams(
          node.id,
          { osc: { ...(plan.nodeParams[node.id]?.osc ?? {}), ...patch } },
          groupPaths("osc", patch),
        );
      // OSCILLATOR menu order (device top-left → bottom-right): Mode, ON, then the
      // Frequency / Level row (Frequency shows only in Sine Wave mode).
      const ps = section(m.inspector.parameters, { key: "params" });
      ps.body.append(
        enumSelect(m.inspector.oscMode, OSC_MODE_OPTIONS, osc.mode ?? OSC_MODE_SINE, (v) => setOsc({ mode: v })),
      );
      ps.body.append(boolToggle(m.inspector.oscOn, osc.on ?? false, (v) => setOsc({ on: v })));
      const oscMode = osc.mode ?? OSC_MODE_SINE;
      if (oscMode === OSC_MODE_SINE) {
        ps.body.append(eqFreqControl(osc.freq ?? 1000, (hz) => setOsc({ freq: hz })));
      } else if (oscMode === OSC_MODE_BURST) {
        ps.body.append(
          rangeSlider(
            m.inspector.oscWidth,
            0.1,
            10,
            0.1,
            osc.width ?? 0.1,
            (v) => `${v.toFixed(1)} s`,
            (v) => setOsc({ width: v }),
          ),
        );
        ps.body.append(
          rangeSlider(
            m.inspector.oscInterval,
            1,
            30,
            1,
            osc.interval ?? 1,
            (v) => `${v} s`,
            (v) => setOsc({ interval: v }),
          ),
        );
      }
      ps.body.append(
        rangeSlider(m.inspector.oscLevel, -96, 0, 1, osc.level ?? -14, formatDb, (v) => setOsc({ level: v })),
      );
      host.append(ps.el);
    }

    // STREAMING DELAY (bus.stream): on / time / frame rate. DELAY screen order
    // (device top-left → bottom-right): Frame rate, ON, then the Delay Time knob.
    if (node.id === "bus.stream") {
      const delay = plan.nodeParams[node.id]?.delay ?? {};
      // Read the latest stored delay at edit time so a second field edit (the
      // inspector is not re-rendered between edits) does not clobber the first. The time
      // the plan takes is the one the unit holds, on its 0.01 ms grid.
      const setDelay = (patch: Partial<typeof delay>): void => {
        if (patch.time !== undefined) patch = { ...patch, time: vdToDelayTime(delayTimeToVd(patch.time)) };
        actions.onUpdateNodeParams(
          node.id,
          { delay: { ...(plan.nodeParams[node.id]?.delay ?? {}), ...patch } },
          groupPaths("delay", patch),
        );
      };
      const ps = section(m.inspector.delayTitle, { key: "delay" });
      ps.body.append(
        enumSelect(
          m.inspector.delayFrameRate,
          DELAY_FRAME_RATE_OPTIONS,
          delay.frameRate ?? DELAY_FRAME_RATE_DEFAULT,
          (v) => setDelay({ frameRate: v }),
        ),
      );
      ps.body.append(boolToggle(m.inspector.delayOn, delay.on ?? false, (v) => setDelay({ on: v })));
      // The unit's own pressed-knob step, 0.02 ms. A key or a notch moves the held value by
      // exactly that, as the unit's knob does, so a value off the 0.02 ms grid prints, is
      // written and steps as itself; a drag lands on the grid.
      ps.body.append(
        rangeSlider(
          m.inspector.delayTime,
          DELAY_TIME_MIN_MS,
          DELAY_TIME_MAX_MS,
          DELAY_TIME_GRID_MS,
          delay.time ?? DELAY_TIME_MIN_MS,
          (v) => `${v.toFixed(2)} ms`,
          (v) => setDelay({ time: v }),
          true,
        ),
      );
      host.append(ps.el);
    }

    // Insert FX dropdown: MONO IN channels (input effects) and MIX/STEREO outputs
    // (output effects). An option is disabled when it exceeds the current sample
    // rate's ceiling, when the node is on a STEREO-linked pair and the effect runs on a
    // mono channel alone, or when its device-wide 1-of slot is taken by another node —
    // all asked of the one menu in core/constraints.ts the CONSOLE chip asks, so
    // the two screens cannot disagree about what is available. The reason itself is not
    // printed here: a locked option is disabled whatever locked it, and the sentence for
    // each reason is on the CONSOLE row that names them.
    //
    // A collapsible section, and its ON state folds it, exactly as GATE / COMP / EQ and
    // the Ducker do. It was the one processor of the five laid out LOOSE — a bus grouped
    // it into Parameters and a channel had it hanging under the EQ module — which made it
    // the only one whose height could not be put away: 72px on a channel holding nothing
    // and 177px holding something, against the 35px a folded section costs, on every
    // channel, always. Being outside the fold index also meant every row added above it
    // pushed it further down with no way to bring it back.
    const ifx = insertFxControl(model, node.id);
    if (ifx) {
      const ifxMenu = insertFxMenu(model, plan, node.id);
      // Every control here reads the value the device path will act on rather than the raw
      // plan value, the selector included. A node's own control may not carry what the plan
      // holds — an output effect on a channel, a channel effect on a bus — and the emit
      // path turns such a value into No Effect and writes no engine parameter for it, so a
      // bypass switch and an editor over it would change nothing that leaves. The selector
      // shows it for a second reason: the raw value matches no option, and a `<select>`
      // handed one lands at selectedIndex -1 and draws an EMPTY field. The plan keeps the
      // raw value until the operator picks something, and picking writes a real one.
      const ifxEff = effectiveInsertFx(model, plan, node.id);
      // The section's lamp and its fold follow the same state the switch inside it writes:
      // engaged means something is in the path. Selecting engages, so choosing a type
      // opens the section it was chosen in rather than leaving the rest folded away.
      const ifxOn = insertFxEngaged(plan.nodeParams[node.id]);
      const ifxSec = section(m.inspector.insertFx, { open: ifxOn, on: ifxOn, key: "insertFxOn" });
      ifxSec.body.append(
        selectControl(
          m.inspector.insertFxType,
          ifxMenu.map((e) => ({
            value: String(e.option.value),
            label: e.option.label,
            disabled: e.lock !== null,
          })),
          String(ifxEff ?? INSERT_FX_NONE),
          // Selecting an effect engages it and selecting No Effect turns the switch off, as
          // choosing them on the unit's own panel does, so the plan takes both.
          (v) => {
            const sel = Number(v);
            const patch: NodeParams = { insertFx: sel, insertFxOn: sel !== INSERT_FX_NONE };
            const parked = parkOutgoingInsertFxParams(plan.nodeParams[node.id]);
            // The plan takes the selected type's defaults for every slot it does not hold,
            // which is what the screen shows and what the unit fills the engine with.
            const { params, seeded } = seedInsertFxParams(parked ?? undefined, sel);
            if (parked || seeded.length > 0) patch.insertFxParams = params;
            actions.onUpdateNodeParams(
              node.id,
              patch,
              undefined,
              seeded.map((key) => `insertFxParams.${key}`),
            );
          },
        ),
      );
      // ON/OFF (bypass) switch below the selector — hidden under No Effect (the
      // device ignores the switch then, and re-engages it on every selection).
      // Above every effect's ceiling there is no DSP left to bypass: show it locked
      // and OFF, the display/plan split the stereo CH EQ toggle already has. The
      // plan keeps its value and translate.ts keeps emitting it, so the control
      // shows what the operator may change, not what will be written.
      if (ifxEff !== undefined && ifxEff !== INSERT_FX_NONE) {
        // The ceiling that decides this is the SELECTED effect's, not the menu's: Pitch
        // Fix stops at 48 kHz where the amps and companders reach 96. A selection this
        // app's own table does not carry (read back from a unit) has no ceiling to name
        // and falls back to the menu-wide answer.
        const { locked: ifxRateLocked, entry: ifxEntry } = insertFxRateLock(ifxMenu, ifxEff);
        // Through the shared helper the other on-state sections use, so switching it also
        // drops a manual fold and hands the section back to following its own state — and
        // so the row carries no label of its own, the section header being its name.
        ifxSec.body.append(
          sectionToggle(
            node.id,
            "insertFxOn",
            !ifxRateLocked && ifxOn,
            actions,
            m.inspector.insertFx,
            !ifxRateLocked
              ? undefined
              : ifxEntry?.option.maxRate !== undefined
                ? m.inspector.insFxRateLockedAt(ifxEntry.option.label, formatRate(ifxEntry.option.maxRate))
                : m.inspector.insFxRateLocked,
          ),
        );
      }
      // The selected effect's own parameters. This is their launcher and they live on the
      // tuning screen, beside the meters that say what they are doing — the move GATE /
      // COMP / EQ / SSMCS have already made, and for the same reason: a slider built here
      // reads the snapshot taken at render time and writes a stale value back on its next
      // drag. Every family the app edits at all is shown there, so there is no second
      // editor here for one of them to be split across.
      if (ifxEff !== undefined && insertFxScreenFamily(model, plan, node.id)) {
        ifxSec.body.append(dynLauncher("insfx", node.id, actions, m));
      }
      host.append(ifxSec.el);
    }

    // A channel whose jack or source another channel reads says so under its last section.
    const share = node.kind === "channel" ? inputShare(model, plan, node.id) : null;
    if (share) {
      host.append(
        hint((share.kind === "jack" ? m.inspector.shareJack : m.inspector.shareSource)(share.others, share.holder)),
      );
    }

    // Any node may be shelved; its wires are hidden along with it (see graph.ts).
    const hide = document.createElement("button");
    hide.type = "button";
    hide.className = "subtle";
    hide.textContent = m.inspector.hideNode;
    hide.addEventListener("click", () => actions.onHideNode(node.id));
    host.append(hide);
    return;
  }

  // connection
  const { from, to } = selection;
  const conn = plan.connections.find((c) => c.from === from && c.to === to);
  host.append(
    heading(m.inspector.connection),
    field(m.inspector.from, endpointLabel(from)),
    field(m.inspector.to, endpointLabel(to)),
    field(m.inspector.type, connKindLabel(from, to, model)),
  );

  // OSC → bus assign wire: independent L/R on/off for stereo buses; FX buses are
  // mono, so the wire's presence alone is the on state.
  const oscTarget = parseRef(from).nodeId === "bus.osc" ? oscAssign(parseRef(to).nodeId) : null;
  if (conn && oscTarget) {
    host.append(subheading(m.inspector.parameters));
    if (oscTarget.r !== null) {
      host.append(
        boolToggle(m.inspector.oscAssignL, conn.params?.oscL ?? true, (v) =>
          actions.onUpdateParams(from, to, { oscL: v }),
        ),
      );
      host.append(
        boolToggle(m.inspector.oscAssignR, conn.params?.oscR ?? true, (v) =>
          actions.onUpdateParams(from, to, { oscR: v }),
        ),
      );
    } else {
      host.append(hint(m.inspector.selectionOnly));
    }
  } else if (conn) {
    const { fields, busFixed, panLinked } = sendFields(model, plan, conn.kind, from, to);
    // Expose a per-send ON toggle where the route carries one (sendHasOn): the
    // CH/FX → MIX/FX sends and the fixed MIX → STEREO "TO ST". The STEREO main paths
    // do not. `isFixedToSt` only drives the toggle's label / default-off presentation.
    const isFixedToSt = isFixedConnection(model, from, to) && conn.kind === "sendSwitch";
    const hasSendOn = sendHasOn(model, from, to);
    // A send into or out of a bus the sample rate has removed sets values on DSP the unit is
    // not running: every field shows its value and takes no edit, as the CONSOLE's column does.
    const rateGone =
      nodeRateDisabled(parseRef(from).nodeId, plan.sampleRate) ||
      nodeRateDisabled(parseRef(to).nodeId, plan.sampleRate);
    if (fields.length || hasSendOn) {
      host.append(subheading(m.inspector.parameters));
      const at = host.childElementCount;
      // ON first to match the device SEND TO screen order (ON, PRE, Pan, Level).
      // The CH/FX sends ship ON (default true); the TO ST switch ships off.
      if (hasSendOn) {
        const onLabel = isFixedToSt ? m.inspector.toSt : m.inspector.sendOn;
        host.append(
          boolToggle(onLabel, conn.params?.on ?? !isFixedToSt, (v) => actions.onUpdateParams(from, to, { on: v })),
        );
      }
      // A stereo channel's "pan" is a balance; so is a STEREO-linked MONO IN pair
      // in BAL mode. Label it BALANCE to match the device; PAN otherwise.
      const panLabel = isBalanceChannel(model, plan, parseRef(from).nodeId) ? m.inspector.balance : m.inspector.pan;
      // The tap is always editable in the planner (the plan records intent). It is
      // turned read-only only while live-connected and the device cannot accept the
      // write — CH → FX taps (the device rejects a software PRE write); shown
      // disabled with an explanatory tooltip so the value is visible but unchangeable.
      const tapEditable = !liveActive || sendTapWritable(model, from, to);
      for (const f of fields) host.append(paramControl(f, conn, actions.onUpdateParams, panLabel, tapEditable));
      if (rateGone)
        for (const row of [...host.children].slice(at)) holdRow(row as HTMLElement, m.inspector.fx2RateLocked);
    } else {
      host.append(hint(m.inspector[sendlessNote(model, from, to)]));
    }
    if (rateGone && (fields.length || hasSendOn)) host.append(hint(m.inspector.fx2RateLocked));
    if (busFixed) host.append(hint(m.inspector.busFixedSend));
    if (panLinked) host.append(hint(m.inspector.panLinked));
  }

  // A fixed wire (CH / FX channel -> STEREO) cannot be removed; offer no delete
  // button, only a note that it is structural. Its level/pan above stay editable.
  if (isFixedConnection(model, from, to)) {
    host.append(hint(m.inspector.fixedConnection));
    // A PRE (pre-fader) send from a channel whose Ducker is on taps ahead of the
    // Ducker (which sits post-fader), so the send is not ducked — flag it next to
    // the fixed-connection note rather than on the canvas.
    if (conn && sendIsPreFader(model, plan, conn) && channelDuckerOn(model, plan, parseRef(from).nodeId))
      host.append(hint(m.inspector.duckerPreSend));
    return;
  }

  // The last source of a receiver the unit never leaves without one (STREAMING) is not deleted
  // either: it is replaced by drawing another source onto the receiver, which the note says in
  // the words the board's own refusal uses.
  if (isLastRequiredSource(model, plan, from, to)) {
    host.append(hint(m.status.streamingSourceRequired));
    return;
  }

  const del = document.createElement("button");
  del.type = "button";
  del.className = "danger";
  del.textContent = m.inspector.deleteConnection;
  del.addEventListener("click", () => actions.onDeleteConnection(from, to));
  host.append(del);
}

function connKindLabel(from: string, to: string, model: DeviceModel): string {
  const rule = model.rules.find((r) => r.from === from && r.to === to);
  if (rule?.kind === "patch" && monoPairsInto(model, to).length > 0) return t().inspector.connKindMonoPair;
  return rule ? t().inspector.connKind[rule.kind] : t().inspector.none;
}

function nodeKindLabel(kind: NodeKind): string {
  return t().inspector.nodeKind[kind];
}

function heading(text: string): HTMLElement {
  const h = document.createElement("h2");
  setNamedText(h, text);
  return h;
}

function closeButton(label: string, onClose: () => void): HTMLElement {
  const ns = "http://www.w3.org/2000/svg";
  const b = document.createElement("button");
  b.type = "button";
  b.className = "inspector-close";
  b.setAttribute("aria-label", label);
  b.title = label;
  // Decorative ✕ glyph; the accessible name comes from aria-label.
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("viewBox", "0 0 18 18");
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS(ns, "path");
  path.setAttribute("d", "M5 5l8 8M13 5l-8 8");
  path.setAttribute("fill", "none");
  path.setAttribute("stroke", "currentColor");
  path.setAttribute("stroke-width", "1.6");
  path.setAttribute("stroke-linecap", "round");
  svg.append(path);
  b.append(svg);
  b.addEventListener("click", onClose);
  return b;
}

function subheading(text: string): HTMLElement {
  const h = document.createElement("h3");
  h.textContent = text;
  return h;
}

// The bare ON/OFF control for an on-state section (GATE / COMP / EQ / Ducker):
// its name is the section header, so the label is empty. Toggling the value
// drops any manual fold so the section reverts to following the new on-state.
function sectionToggle(
  nodeId: string,
  key: string,
  on: boolean,
  actions: InspectorActions,
  name: string,
  lockedTitle?: string,
): HTMLElement {
  // A lockedTitle shows the value with both buttons disabled + a tooltip (e.g. the
  // stereo EQ above 96 kHz) — mirrors the read-only CH → FX tap control.
  return boolToggle(
    "",
    on,
    (v) => {
      clearSectionOverride(key);
      actions.onUpdateNodeParams(nodeId, { [key]: v });
    },
    lockedTitle,
    name,
  );
}

// A collapsible inspector section (rack-module style): a summary header in the
// h3 mono-uppercase idiom, optionally carrying an ON led that mirrors a section
// toggle (GATE / COMP / EQ), and a body the caller fills. Built on <details> so
// it gets native keyboard toggling and respects reduced motion. `open` is the
// default disclosure state; a persisted `key` override wins over it, so an off
// section (on === false) collapses on its own until the user folds it by hand.
function section(
  title: string,
  opts: { open?: boolean; on?: boolean; key?: string } = {},
): { el: HTMLDetailsElement; body: HTMLElement } {
  const el = document.createElement("details");
  el.className = "insp-section";
  const key = opts.key;
  const initial = resolveSectionOpen(key, opts.open ?? true);
  el.open = initial;
  if (key !== undefined) {
    // The property set above can queue one echo toggle event; skip it by only
    // persisting when the disclosure actually leaves the value we last applied.
    let expected = initial;
    el.addEventListener("toggle", () => {
      if (el.open === expected) return;
      expected = el.open;
      recordSectionOpen(key, el.open);
    });
  }
  const sum = document.createElement("summary");
  if (opts.on !== undefined) {
    const led = document.createElement("span");
    led.className = "sec-led" + (opts.on ? " on" : "");
    sum.append(led);
  }
  const label = document.createElement("span");
  label.className = "sec-title";
  label.textContent = title;
  sum.append(label);
  el.append(sum);
  const body = document.createElement("div");
  body.className = "sec-body";
  el.append(body);
  return { el, body };
}

function hint(text: string): HTMLElement {
  const p = document.createElement("p");
  p.className = "hint";
  p.textContent = text;
  return p;
}

function field(label: string, value: string): HTMLElement {
  const row = document.createElement("div");
  row.className = "field";
  const k = document.createElement("span");
  k.className = "field-key";
  k.textContent = label;
  const v = document.createElement("span");
  v.className = "field-val";
  v.textContent = value;
  row.append(k, v);
  return row;
}

type UpdateParams = (from: string, to: string, patch: ConnParams) => void;

/** Show a built control row's value and refuse its edit: every button, select and input in it
 *  disabled, with the reason as the row's tooltip. */
function holdRow(row: HTMLElement, title: string): void {
  row.title = title;
  for (const c of row.querySelectorAll<HTMLButtonElement | HTMLSelectElement | HTMLInputElement>(
    "button, select, input",
  ))
    c.disabled = true;
}

function paramControl(
  field: ParamField,
  conn: PlanConnection,
  onUpdate: UpdateParams,
  panLabel: string,
  tapEditable = true,
): HTMLElement {
  if (field === "tap") return tapControl(conn, onUpdate, tapEditable);
  return field === "level"
    ? levelSlider(t().inspector.level, conn.params?.level ?? 0, (v) => onUpdate(conn.from, conn.to, { level: v }))
    : panSlider(conn, onUpdate, panLabel);
}

// A labeled range slider that updates its value readout and reports the numeric
// value on every input. Mutates in place (no re-render) so it keeps focus while
// dragging. Shared by the connection (panSlider) and node-level controls.
//
// With `heldSteps`, an arrow key or a wheel notch moves the value the row holds by exactly
// `step` and stops at the ends, so a value off the step grid stays off it; a drag lands on
// the grid. The range itself cannot hold such a value — it rounds one to the nearer step,
// halfway up — so the row keeps it, and the thumb rests where the range rounds it.
function rangeSlider(
  label: string,
  min: number,
  max: number,
  step: number,
  cur: number,
  fmt: (v: number) => string,
  onInput: (v: number) => void,
  heldSteps = false,
): HTMLElement {
  const { row, value, labelId: id } = paramBlock(label, fmt(cur));
  const slider = document.createElement("input");
  slider.type = "range";
  slider.min = String(min);
  slider.max = String(max);
  slider.step = String(step);
  slider.value = String(cur);
  nameBy(slider, id);
  slider.setAttribute("aria-valuetext", fmt(cur));
  let held = cur;
  const write = (v: number): void => {
    held = v;
    setLevelText(value, fmt(v));
    slider.setAttribute("aria-valuetext", fmt(v));
    onInput(v);
  };
  slider.addEventListener("input", () => write(Number(slider.value)));
  if (heldSteps) {
    const stepHeld = (dir: 1 | -1): void => {
      if (slider.disabled) return;
      const next = Math.min(max, Math.max(min, scrubFloat(held + dir * step)));
      if (next === held) return;
      slider.value = String(next);
      write(next);
    };
    slider.addEventListener("keydown", (e) => {
      const dir =
        e.key === "ArrowUp" || e.key === "ArrowRight" ? 1 : e.key === "ArrowDown" || e.key === "ArrowLeft" ? -1 : 0;
      if (dir === 0 || e.altKey || e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      stepHeld(dir);
    });
    onWheelStep(slider, stepHeld);
  } else {
    wheelStep(slider);
  }
  holdInertOnBlur(slider);
  row.append(slider);
  return row;
}

// The connection PAN / BALANCE slider (the only per-send slider besides LEVEL):
// signed ±63, center-0 default, formatted L / C / R.
function panSlider(conn: PlanConnection, onUpdate: UpdateParams, label: string): HTMLElement {
  return rangeSlider(label, PAN_MIN, PAN_MAX, 1, conn.params?.pan ?? 0, formatPan, (v) =>
    onUpdate(conn.from, conn.to, { pan: v }),
  );
}

/** The dotted paths a group rebuild ASSERTED: the fields the caller set, not the whole
 *  group it rebuilt. Naming the group would claim every sibling it merely copied, and the
 *  merge drops a named group whole — measured, a device's OSC frequency read during an
 *  OSC ON toggle was thrown away with it. */
function groupPaths(group: string, patch: object): string[] {
  return Object.keys(patch).map((key) => `${group}.${key}`);
}

// Node-level gain slider (HA / D.Gain): integer dB steps over the given range.
function gainControl(label: string, min: number, max: number, cur: number, onChange: (v: number) => void): HTMLElement {
  return rangeSlider(label, min, max, 1, cur, formatGainDb, onChange);
}

// FX-channel EFFECT section: the EFFECT TYPE selector and the launcher. The type's own
// parameters are the tuning screen's, so nothing here reads a value that screen can move.
// fxIndex = 0 (FX1) / 1 (FX2).
function fxEffectSection(
  nodeId: string,
  fxIndex: number,
  plan: Plan,
  actions: InspectorActions,
  m: Messages,
): HTMLElement {
  const t = m.inspector.fxEffect;
  const fx = plan.nodeParams[nodeId]?.fxEffect ?? {};
  // Resolved against THIS channel's menu, so the selector shows the effect the write
  // path would send rather than one the channel does not offer (translate.ts).
  const type = resolveFxEffectType(fxIndex, fx.type);
  const { el, body } = section(t.title, { key: "fxEffect" });

  const select = enumSelect(
    t.effectType,
    fxEffectTypes(fxIndex),
    type,
    (v) => actions.onSetFxEffectType(nodeId, v),
    nodeRateDisabled(nodeId, plan.sampleRate),
  );
  if (nodeRateDisabled(nodeId, plan.sampleRate)) select.title = m.inspector.fx2RateLocked;
  body.append(select);
  // The type's own parameters moved to the tuning screen, for the reason stated on
  // `dynLauncher`: they belong beside the meters either side of the effect, and a second
  // copy here would sit at the position it was drawn at — these sliders read a captured
  // snapshot and do not redraw when a value changes — and write that stale value back on
  // the next drag. GATE / COMP / EQ / SSMCS / DUCKER gave theirs up for the same reason.
  body.append(dynLauncher("fx", nodeId, actions, m));
  return el;
}

// Ducker node section: the on/off and the control that opens its tuning screen. The
// ducker source is a key-source connection, edited on the canvas, not here.
function duckerBlock(nodeId: string, np: NodeParams, plan: Plan, actions: InspectorActions, m: Messages): HTMLElement {
  const on = processorOn(np, "duckerOn");
  const { el, body } = section(m.inspector.duckerOn, { open: on, on, key: "duckerOn" });
  body.append(sectionToggle(nodeId, "duckerOn", on, actions, m.inspector.duckerOn));
  // The detail sliders moved to the tuning screen, for the reason stated on
  // `dynLauncher`: they belong beside the meters that say what they are doing, and a
  // second copy here would sit at a stale position after the screen moved a value and
  // write it back on the next drag.
  body.append(dynLauncher("ducker", nodeId, actions, m));
  return el;
}

// GATE / COMP / EQ / SSMCS section body for a MONO IN channel: the ON toggle (added
// by the caller) and the control that opens that processor's tuning screen. The detail
// sliders used to live here; they moved to that screen, which can show them beside
// the meters that say what they are doing. Keeping a second copy here would not
// just duplicate them — a slider built here reads the params snapshot captured at
// render time and never re-renders on a value change, so after the screen moved
// the threshold these sliders would sit at the old position and write it back on
// the next drag. That is what the SSMCS sliders did until they moved too.
function dynLauncher(kind: DynKind, nodeId: string, actions: InspectorActions, m: Messages): HTMLElement {
  const btn = document.createElement("button");
  btn.className = "gate-open";
  btn.id = `btn-${kind}-screen`;
  btn.textContent = dynOpenLabel(kind, m);
  btn.addEventListener("click", () => actions.onOpenDynScreen(kind, nodeId));
  return btn;
}

// Merge a patch into a node's SSMCS sub-object — at the top level, a named nested
// sub-section (comp / sc), or a named EQ band — reading the latest stored value
// so sibling slider edits aren't lost.
function mergeSsmcs(actions: InspectorActions, plan: Plan, nodeId: string, patch: Partial<SsmcsParams>): void {
  actions.onUpdateNodeParams(
    nodeId,
    { ssmcs: { ...(plan.nodeParams[nodeId]?.ssmcs ?? {}), ...patch } },
    groupPaths("ssmcs", patch),
  );
}

// A range slider over discrete integer positions: the slider walks [0, posMax] by
// 1, and toPos/fromPos map a domain value (dB, Hz, …) to and from a position. Used
// where the domain is non-linear or a fixed grid, so every stop is a real value.
//
// With `step`, the keys and the wheel move from the value the row holds rather than from
// the slider's position, which is the grid point NEAREST that value: `step(from, delta)`
// answers the value `delta` grid points away. Arrow keys move one point (Up / Right raise),
// PageUp / PageDown six, Home / End go to position 0 / posMax, and each wheel detent is one
// point, the wheel-step preference deciding how many detents a notch is.
function snappedSlider(
  label: string,
  cur: number,
  posMax: number,
  toPos: (v: number) => number,
  fromPos: (pos: number) => number,
  fmt: (v: number) => string,
  onChange: (v: number) => void,
  step?: (from: number, delta: number) => number,
): HTMLElement {
  const { row, value, labelId: id } = paramBlock(label, fmt(cur));
  const slider = document.createElement("input");
  slider.type = "range";
  slider.min = "0";
  slider.max = String(posMax);
  slider.step = "1";
  slider.value = String(toPos(cur));
  nameBy(slider, id);
  // The position is a grid index; what it stands for is the value the readout prints.
  slider.setAttribute("aria-valuetext", fmt(cur));
  let held = cur;
  const show = (v: number): void => {
    held = v;
    setLevelText(value, fmt(v));
    slider.setAttribute("aria-valuetext", fmt(v));
  };
  slider.addEventListener("input", () => {
    const v = fromPos(Number(slider.value));
    show(v);
    onChange(v);
  });
  if (step) {
    const go = (next: number): void => {
      if (slider.disabled || next === held) return;
      slider.value = String(toPos(next));
      show(next);
      onChange(next);
    };
    const KEY_STEPS: Record<string, number> = {
      ArrowUp: 1,
      ArrowRight: 1,
      ArrowDown: -1,
      ArrowLeft: -1,
      PageUp: 6,
      PageDown: -6,
    };
    slider.addEventListener("keydown", (e) => {
      const delta = KEY_STEPS[e.key];
      const next =
        delta !== undefined
          ? step(held, delta)
          : e.key === "Home"
            ? fromPos(0)
            : e.key === "End"
              ? fromPos(posMax)
              : null;
      if (next === null) return;
      e.preventDefault();
      go(next);
    });
    onWheelStep(slider, (dir) => go(step(held, dir)));
  } else {
    wheelStep(slider);
  }
  holdInertOnBlur(slider);
  row.append(slider);
  return row;
}

// EQ band frequency slider on a log scale (20 Hz … 20 kHz) so each octave gets
// equal width; reports the snapped Hz value and formats as Hz / kHz.
function eqFreqControl(cur: number, onChange: (hz: number) => void): HTMLElement {
  return snappedSlider(t().inspector.frequency, cur, EQ_FREQ_POS_MAX, eqFreqToPos, eqPosToHz, formatHz, onChange);
}

// A level / send / fader slider that snaps to the device's discrete level_gain
// grid (LEVEL_STEPS_DB) instead of a uniform dB step — the hardware only stores
// those detents, so a 0.5 dB step would offer unsettable values (e.g. -15.0).
// The slider index maps to a grid position: 0 = -∞ (off), 1..N = LEVEL_STEPS_DB.
// Its keys and wheel step through stepLevel from the plan's own value, so a level between two
// detents moves to the adjacent one; a level below the floor steps from -∞.
function levelSlider(label: string, cur: number, onChange: (v: number) => void): HTMLElement {
  return snappedSlider(label, cur, LEVEL_POS_MAX, levelToPos, posToLevel, formatDb, onChange, (from, delta) =>
    stepLevel(from < LEVEL_MIN_DB ? LEVEL_OFF_DB : from, delta),
  );
}

// Node-level bus output fader (STEREO master / MIX / MONITOR): the level_gain
// scale, -∞ then -96.0 … +10.0 dB — the same as a send. The bottom notch
// is the -∞ / off position.
function faderControl(cur: number, onChange: (v: number) => void): HTMLElement {
  return levelSlider(t().inspector.level, cur, onChange);
}

// Node-level bus master balance (STEREO 583 / MIX 676): signed ±63, center 0.
function balanceControl(label: string, cur: number, onChange: (v: number) => void): HTMLElement {
  return rangeSlider(label, PAN_MIN, PAN_MAX, 1, cur, formatPan, onChange);
}

// A two-button ON/OFF toggle for a node-level boolean (channel on, HPF), styled
// like the PRE/POST control. Highlights the active state and reports the chosen
// value on click.
// Mark `button` as the pressed one in a two-button toggle group (clears the
// others). Lets a toggle reflect a click at once: callers that mutate in place
// (e.g. a plain send ON/OFF) do not re-render the inspector, so without this the
// button would stay visually stale until the next selection.
function selectToggle(group: HTMLElement, button: HTMLButtonElement): void {
  group.querySelectorAll("button").forEach((x) => {
    x.classList.toggle("on", x === button);
    x.setAttribute("aria-pressed", String(x === button));
  });
}

/** A node's [CUE] row. Its buttons keep their handlers whatever the session, since what is
 *  cued and whether a press switches it are repainted in place (syncInspectorCue) rather than
 *  by rebuilding the panel around the controls the operator may be in. */
function cueRow(id: string, state: CueRowState, actions: InspectorActions, m: Messages): HTMLElement {
  const row = boolToggle(m.inspector.cue, state.on, (v) => actions.onSetCue(id, v));
  row.dataset.cueNode = id;
  paintCueRow(row, state, m);
  return row;
}

type CueRowState = { on: boolean; enabled: boolean };

/** Show `state` on a CUE row: the lit button, and read-only with the reason as its tooltip
 *  while a press switches nothing. */
function paintCueRow(row: HTMLElement, state: CueRowState, m: Messages): void {
  const [on, off] = row.querySelectorAll<HTMLButtonElement>(".toggle button");
  for (const [b, lit] of [
    [on, state.on],
    [off, !state.on],
  ] as const) {
    b.classList.toggle("on", lit);
    b.setAttribute("aria-pressed", String(lit));
    b.disabled = !state.enabled;
  }
  if (state.enabled) row.removeAttribute("title");
  else row.title = m.inspector.cueOffline;
}

/** Repaint the CUE row of a panel already on screen, if it carries one, from `cueOf`. Nothing
 *  else in the panel is touched. */
export function syncInspectorCue(host: HTMLElement, cueOf: InspectorActions["cueOf"]): void {
  for (const row of host.querySelectorAll<HTMLElement>("[data-cue-node]")) {
    const state = cueOf(row.dataset.cueNode!);
    if (state) paintCueRow(row, state, t());
  }
}

/** Repaint the Clip Safe row of a panel already on screen, if it carries one, for whether its jack's
 *  Clip Safe is holding the gain down. Nothing else in the panel is touched. */
export function markClipSafe(host: HTMLElement, engaged: (jack: number) => boolean): void {
  for (const row of host.querySelectorAll<HTMLElement>("[data-clip-safe-jack]"))
    paintClipSafeRow(row, engaged(Number(row.dataset.clipSafeJack)));
}

/** While its jack's Clip Safe holds the gain down, the row's ON takes the Clip Safe colour and says
 *  so to a reader and on hover. */
function paintClipSafeRow(row: HTMLElement, engaged: boolean): void {
  const on = row.querySelector<HTMLButtonElement>(".toggle button");
  if (!on) return;
  on.classList.toggle("clip-safe", engaged);
  if (engaged) {
    on.setAttribute("aria-description", t().inspector.clipSafeEngaged);
    on.title = t().inspector.clipSafeEngaged;
  } else {
    on.removeAttribute("aria-description");
    on.removeAttribute("title");
  }
}

/** A two-button group, named by its row's label or, for a section's bare toggle, by the
 *  section's own name. */
function toggleGroup(labelId: string | undefined, name?: string): HTMLElement {
  const group = document.createElement("div");
  group.className = "toggle";
  group.setAttribute("role", "group");
  if (labelId !== undefined) group.setAttribute("aria-labelledby", labelId);
  else if (name !== undefined) group.setAttribute("aria-label", name);
  return group;
}

// `lockedTitle`, when set, renders the pair read-only: both buttons disabled (no
// click handler) and the reason shown as a row tooltip — the value is still visible.
// `name` names a pair that has no row label of its own (a section's bare toggle).
/** The two-button switch at the top of a stereo channel's INPUT section: which side the
 *  panel shows. A press on the side already shown changes nothing. */
function sideSwitch(model: DeviceModel, id: string, actions: InspectorActions, m: Messages): HTMLElement {
  const shown = displayedSide(id);
  const bar = document.createElement("div");
  bar.className = "side-switch";
  bar.setAttribute("role", "group");
  bar.setAttribute("aria-label", m.inspector.sideGroup);
  for (const side of ["L", "R"] as const) {
    const b = document.createElement("button");
    b.type = "button";
    setNamedText(b, (side === "L" ? m.inspector.sideL : m.inspector.sideR)(sideLabel(model, id, side)));
    b.setAttribute("aria-pressed", String(side === shown));
    b.addEventListener("click", () => actions.onSwitchSide(id, side));
    bar.append(b);
  }
  return bar;
}

function boolToggle(
  label: string,
  value: boolean,
  onChange: (v: boolean) => void,
  lockedTitle?: string,
  name?: string,
  spoken?: string,
): HTMLElement {
  // Read as truthiness, not `=== on`, so a value that reaches here as a number (1 / 0)
  // lights and presses the button the write would send.
  const state = Boolean(value);
  const { row, labelId: id } = paramBlock(label, "", spoken);
  if (lockedTitle !== undefined) row.title = lockedTitle;
  // A row whose label is a symbol names its pair by the word instead of by the label.
  const group = spoken === undefined ? toggleGroup(id, name) : toggleGroup(undefined, spoken);
  const make = (on: boolean, text: string): HTMLButtonElement => {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = text;
    b.classList.toggle("on", state === on);
    b.setAttribute("aria-pressed", String(state === on));
    if (lockedTitle === undefined)
      b.addEventListener("click", () => {
        selectToggle(group, b);
        onChange(on);
      });
    else b.disabled = true;
    return b;
  };
  group.append(make(true, t().inspector.on), make(false, t().inspector.off));
  row.append(group);
  return row;
}

// A labeled dropdown for an enum node parameter (e.g. insert FX). Reports the
// chosen option's value string on change.
function selectControl(
  label: string,
  options: { value: string; label: string; disabled?: boolean }[],
  current: string,
  onChange: (v: string) => void,
  disabled = false,
): HTMLElement {
  const { row, labelId: id } = paramBlock(label, "");
  const sel = document.createElement("select");
  nameBy(sel, id);
  for (const o of options) {
    const opt = document.createElement("option");
    opt.value = o.value;
    opt.textContent = o.label;
    if (o.disabled) opt.disabled = true;
    sel.append(opt);
  }
  sel.value = current;
  sel.disabled = disabled;
  sel.addEventListener("change", () => onChange(sel.value));
  row.append(sel);
  return row;
}

// selectControl over a numeric-value option list (the {value:number,label} enum
// shape used across params.ts), handling the string<->number coercion so call
// sites stay free of String()/Number() boilerplate.
function enumSelect(
  label: string,
  options: { value: number; label: string }[],
  current: number,
  onChange: (v: number) => void,
  disabled = false,
): HTMLElement {
  return selectControl(
    label,
    options.map((o) => ({ value: String(o.value), label: o.label })),
    String(current),
    (v) => onChange(Number(v)),
    disabled,
  );
}

// Channel/bus color palette for the top-accent cap — the device CH SETTING
// palette so a chosen color maps 1:1 to the on-device color (and reads back to
// the same swatch). Order follows the device step list (COLOR_PALETTE); the
// "none" swatch is the device "Off" state (no cap).
const NODE_COLORS = COLOR_PALETTE.map((c) => c.hex);

/** The unit's own name for each palette entry, in COLOR_PALETTE's order. */
export function colorNames(m: Messages): string[] {
  const c = m.inspector.colorName;
  return [c.blue, c.orange, c.yellow, c.purple, c.cyan, c.magenta, c.red, c.green, c.ltGreen, c.white];
}

// A row of color swatches plus a "none" option, which sets the device Off. The active color
// (or none) is ringed and pressed; each swatch is named the way the unit's picker names it.
// Selecting toggles: clicking the active color sets Off.
function colorSwatches(
  label: string,
  current: string | undefined,
  onPick: (color: string | null) => void,
): HTMLElement {
  const { row, labelId: id } = paramBlock(label, "");
  const strip = document.createElement("div");
  strip.className = "swatches";
  strip.setAttribute("role", "group");
  nameBy(strip, id);
  // What the plan holds, as the swatch it paints: none for Off and for a node with no color.
  const shown = planColorHex(current);
  const swatch = (name: string, selected: boolean): HTMLButtonElement => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "swatch" + (selected ? " sel" : "");
    b.title = name;
    b.setAttribute("aria-label", name);
    b.setAttribute("aria-pressed", String(selected));
    return b;
  };
  const none = swatch(t().inspector.colorName.off, !shown);
  none.classList.add("swatch-none");
  none.addEventListener("click", () => onPick(COLOR_OFF));
  strip.append(none);
  const names = colorNames(t());
  for (const [i, c] of NODE_COLORS.entries()) {
    const b = swatch(names[i], shown === c);
    b.style.background = c;
    b.addEventListener("click", () => onPick(shown === c ? COLOR_OFF : c));
    strip.append(b);
  }
  row.append(strip);
  return row;
}

// A labeled single-line text field. Reports every keystroke (trimmed by the
// caller) without re-rendering, so it keeps focus while typing.
//
// `clip` bounds the value the caller is handed AND the value the box shows, which
// has to be one act: the caller does not re-render this row (that is what keeps
// focus), so a caller that cut the value on its own would leave the field showing
// text the plan does not hold — until some unrelated re-render silently shortened
// it under the operator. `maxlength` cannot do this job alone, because it counts
// UTF-16 units while the device's field is a byte count: 63 of it admits 63
// Japanese characters, which are 189 bytes.
//
// Held during an IME composition and applied at its end. Rewriting `value` mid
// composition takes the text out from under the conversion candidates.
function textInput(
  label: string,
  value: string,
  placeholder: string,
  onInput: (v: string) => void,
  clip?: (v: string) => string,
): HTMLElement {
  const { row, labelId: id } = paramBlock(label, "");
  const input = document.createElement("input");
  input.type = "text";
  nameBy(input, id);
  input.value = value;
  input.placeholder = placeholder;
  let composing = false;
  const report = (): void => {
    if (clip && !composing) {
      const cut = clip(input.value);
      if (cut !== input.value) {
        // Assigning `value` moves the caret to the end of the field, so an edit made
        // in the MIDDLE of a name already at the bound would take the operator's next
        // keystroke to the tail instead of continuing where they were. The cut comes
        // off the tail, so the caret keeps its own position unless it was past the
        // new end.
        const at = input.selectionStart;
        input.value = cut;
        if (at !== null) {
          const p = Math.min(at, cut.length);
          input.setSelectionRange(p, p);
        }
      }
    }
    onInput(input.value);
  };
  input.addEventListener("compositionstart", () => void (composing = true));
  input.addEventListener("compositionend", () => {
    composing = false;
    report();
  });
  input.addEventListener("input", report);
  row.append(input);
  return row;
}

function tapControl(conn: PlanConnection, onUpdate: UpdateParams, editable = true): HTMLElement {
  const cur = conn.params?.tap ?? "post";
  const { row, labelId: id } = paramBlock(t().inspector.prePost, "");
  const group = toggleGroup(id);
  // CH → FX taps are read-only: the device rejects software writes, so the device
  // (LCD) value is shown but the buttons are disabled, with an explanatory tooltip.
  if (!editable) row.title = t().inspector.prePostLcdOnly;
  const make = (tap: "pre" | "post", text: string): HTMLButtonElement => {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = text;
    b.classList.toggle("on", cur === tap);
    b.setAttribute("aria-pressed", String(cur === tap));
    b.disabled = !editable;
    if (editable)
      b.addEventListener("click", () => {
        selectToggle(group, b);
        onUpdate(conn.from, conn.to, { tap });
      });
    return b;
  };
  group.append(make("pre", "PRE"), make("post", "POST"));
  row.append(group);
  return row;
}

// `spoken`, when set, is the word a symbol `labelText` is announced as — the row's control
// is labelled by that word rather than by the symbol's character name.
function paramBlock(
  labelText: string,
  valueText: string,
  spoken?: string,
): { row: HTMLElement; value: HTMLElement; labelId: string | undefined } {
  const row = document.createElement("div");
  row.className = "param";
  // What main.ts keys a carried-over focus by, stamped while the row is being built so
  // the restore does not search the row for its label element per candidate control.
  row.dataset.paramLabel = labelText;
  const value = document.createElement("span");
  value.className = "param-val";
  setLevelText(value, valueText);
  // An empty label + value (a bare section ON/OFF toggle whose name is in the
  // section header) skips the label row so the toggle sits flush. The label carries an id
  // the row's control names itself by.
  let id: string | undefined;
  if (labelText !== "" || valueText !== "") {
    const head = document.createElement("div");
    head.className = "param-label";
    const label = document.createElement("span");
    label.textContent = labelText;
    if (spoken) label.setAttribute("aria-hidden", "true");
    id = labelId("insp-lbl");
    label.id = id;
    head.append(label, value);
    row.append(head);
  }
  return { row, value, labelId: id };
}

/** Name a control by its row's label, where the row has one. */
function nameBy(control: HTMLElement, id: string | undefined): void {
  if (id !== undefined) control.setAttribute("aria-labelledby", id);
}

// Inline warning that the selected node's values were not read from the device
// (shown after a partial readback). Reuses the warning box styling (--warn).
/** The factory value of one input-stage key on its holder, or `fallback` where the model's
 *  factory values carry none. */
function factoryGain(modelId: ModelId, holder: string, key: string, fallback: number): number {
  const v = (factoryNodeParams(modelId, holder) as Record<string, unknown> | undefined)?.[key];
  return typeof v === "number" ? v : fallback;
}

function notReadBadge(text: string): HTMLElement {
  const box = document.createElement("div");
  box.className = "warning";
  const line = document.createElement("div");
  line.className = "warning-line";
  line.textContent = text;
  box.append(line);
  return box;
}

function warningBox(title: string, lines: string[]): HTMLElement {
  const box = document.createElement("div");
  box.className = "warning";
  const head = document.createElement("div");
  head.className = "warning-title";
  head.textContent = title;
  box.append(head);
  for (const l of lines) {
    const line = document.createElement("div");
    line.className = "warning-line";
    line.textContent = l;
    box.append(line);
  }
  return box;
}

function recentRow(entry: RecentEntry, onOpen: (path: string) => void): HTMLElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "recent-row";
  btn.title = entry.path;
  const name = document.createElement("span");
  name.className = "recent-name";
  name.textContent = entry.name;
  const model = document.createElement("span");
  model.className = "recent-model";
  model.textContent = entry.modelId;
  btn.append(name, model);
  btn.addEventListener("click", () => onOpen(entry.path));
  return btn;
}

// Color legend for the empty inspector: wire colors by connection kind and rail
// colors by node kind. Swatch colors come from theme-aware CSS variables so
// they track the active palette (style.css :root / [data-theme="light"]).
function legendBlock(m: Messages): DocumentFragment {
  const frag = document.createDocumentFragment();
  frag.append(subheading(m.inspector.legend.signals));
  frag.append(legendRow("var(--w-select)", m.inspector.legend.source));
  frag.append(legendRow("var(--w-send)", m.inspector.legend.send));
  frag.append(legendPreRow(m.inspector.legend.pre));
  frag.append(legendRow("var(--w-out)", m.inspector.legend.patch));
  frag.append(legendRecPointRow(m.inspector.legend.recPoint));
  frag.append(subheading(m.inspector.legend.nodes));
  frag.append(legendRow("var(--rail-input)", m.inspector.nodeKind.input, true));
  frag.append(legendRow("var(--rail-channel)", m.inspector.nodeKind.channel, true));
  frag.append(legendRow("var(--rail-bus)", m.inspector.nodeKind.bus, true));
  frag.append(legendRow("var(--rail-output)", m.inspector.nodeKind.output, true));
  frag.append(legendRow("var(--rail-ducker)", m.inspector.nodeKind.ducker, true));
  return frag;
}

const SVG_NS = "http://www.w3.org/2000/svg";

// A legend row whose swatch is drawn rather than a plain colored dot, for entries
// whose meaning is a shape (a dash pattern, an exit point) that a dot cannot carry.
// The 20x12 box is shared by every such row so the label column stays flush.
function legendSwatchRow(label: string, draw: () => SVGElement[]): HTMLElement {
  const row = document.createElement("div");
  row.className = "conn-row";
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("width", "20");
  svg.setAttribute("height", "12");
  svg.setAttribute("viewBox", "0 0 20 12");
  svg.style.flexShrink = "0";
  svg.append(...draw());
  const text = document.createElement("span");
  text.textContent = label;
  row.append(svg, text);
  return row;
}

// The PRE swatch mirrors the on-canvas marker: a dashed send wire with an amber
// tap glyph. Theme colors come from CSS variables via inline style.
function legendPreRow(label: string): HTMLElement {
  return legendSwatchRow(label, () => {
    const line = document.createElementNS(SVG_NS, "line");
    line.setAttribute("x1", "0");
    line.setAttribute("y1", "8");
    line.setAttribute("x2", "20");
    line.setAttribute("y2", "8");
    line.setAttribute("stroke-width", "2");
    line.setAttribute("stroke-dasharray", "5 3");
    line.setAttribute("stroke-linecap", "round");
    line.style.stroke = "var(--w-send)";
    const tri = document.createElementNS(SVG_NS, "path");
    tri.setAttribute("d", "M 6 3 L 12 6 L 6 9 Z");
    tri.style.fill = "var(--led)";
    return [line, tri];
  });
}

// The Rec Point swatch mirrors the canvas: a wire leaving the top of a channel
// faceplate through its tap jack, rather than the right edge every other route
// uses. That exit is the whole point of the row, so the swatch draws the strip's
// corner around it.
function legendRecPointRow(label: string): HTMLElement {
  return legendSwatchRow(label, () => {
    const face = document.createElementNS(SVG_NS, "rect");
    face.setAttribute("x", "0.5");
    face.setAttribute("y", "6.5");
    face.setAttribute("width", "11");
    face.setAttribute("height", "5");
    face.setAttribute("rx", "1.5");
    face.setAttribute("fill", "none");
    face.setAttribute("stroke-width", "1");
    face.style.stroke = "var(--rail-channel)";
    const wire = document.createElementNS(SVG_NS, "path");
    wire.setAttribute("d", "M 6 6.5 C 6 2, 12 1, 20 1");
    wire.setAttribute("fill", "none");
    wire.setAttribute("stroke-width", "1.6");
    wire.setAttribute("stroke-linecap", "round");
    wire.style.stroke = "var(--w-out)";
    const jack = document.createElementNS(SVG_NS, "circle");
    jack.setAttribute("cx", "6");
    jack.setAttribute("cy", "6.5");
    jack.setAttribute("r", "1.8");
    jack.style.fill = "var(--w-out)";
    return [face, wire, jack];
  });
}

function legendRow(color: string, label: string, square = false): HTMLElement {
  const row = document.createElement("div");
  row.className = "conn-row";
  const dot = document.createElement("span");
  dot.className = square ? "dot dot-square" : "dot";
  dot.style.background = color;
  const text = document.createElement("span");
  text.textContent = label;
  row.append(dot, text);
  return row;
}

// A wire the board draws is a button that selects it, as a press on the wire does; one it
// does not draw (an end on the shelf, an off send the declutter toggle hides) stays a row.
function connRow(text: string, conn: PlanConnection, actions: InspectorActions): HTMLElement {
  const drawn = actions.wireDrawn(conn.from, conn.to);
  const row = document.createElement(drawn ? "button" : "div");
  row.className = "conn-row";
  if (row instanceof HTMLButtonElement) {
    row.type = "button";
    row.addEventListener("click", () => actions.onSelectConnection(conn.from, conn.to));
  }
  const dot = document.createElement("span");
  // The dot wears the wire's family, not its kind: the routing list and the board
  // have to agree, and the board is drawn from WIRE_GROUP.
  dot.className = `dot dot-${WIRE_GROUP[conn.kind]}`;
  const t = document.createElement("span");
  t.textContent = text;
  row.append(dot, t);
  return row;
}

// One jack's connect picker: the ports a drag from it would be taken on, by name. Choosing
// one draws that wire through the board's own commit.
function connectControl(
  label: string,
  origin: ConnectOrigin,
  endpointLabel: (ref: string) => string,
  actions: InspectorActions,
  m: Messages,
): HTMLElement {
  const { row, labelId: id } = paramBlock(label, "");
  const sel = document.createElement("select");
  sel.dataset.focusKey = `connect:${origin.ref}:${origin.tap ? "tap" : origin.dir}`;
  nameBy(sel, id);
  const prompt = document.createElement("option");
  prompt.value = "";
  prompt.textContent = m.inspector.connectChoose;
  prompt.disabled = true;
  prompt.selected = true;
  sel.append(prompt);
  for (const target of origin.targets) {
    const opt = document.createElement("option");
    opt.value = target;
    opt.textContent = endpointLabel(target);
    sel.append(opt);
  }
  sel.addEventListener("change", () => {
    if (sel.value) actions.onConnect(origin, sel.value);
  });
  row.append(sel);
  return row;
}
